import { inject, Injectable, signal, Signal } from '@angular/core';

import { Notification } from '@models/notification.model';

import {
  PatchableSignal,
  patchableSignal,
} from '@utils/signals/patchableSignal';
import { Observable, Subject, tap } from 'rxjs';
import { NotificationAPI } from '@services/API/notify/notifications.service';
import { API } from '@models/API';

@Injectable({ providedIn: 'root' })
export class NotificationState {
  notificationAPI = inject(NotificationAPI);
  private _notifications: PatchableSignal<Notification[]> = patchableSignal([]);
  public getNotifications: Signal<Notification[]> = this._notifications.asReadonly();
  private readonly toggleMenuSubject = new Subject<Event>()
  public readonly toggleMenuSubject$ = this.toggleMenuSubject.asObservable()
  public notificationCount = signal<API.Notify.Notification.UnreadCount.Response | null>(null)

  /**
   * Fetches the unread count and returns the request, rather than subscribing
   * to it here. LEN-2065: the caller polls this, and it can only pace itself --
   * back off, stop while the tab is hidden -- if it can see when a request
   * finished.
   */
  public refreshCount(): Observable<API.Notify.Notification.UnreadCount.Response> {
    return this.notificationAPI.getCount(false).pipe(
      tap(res => {
        // Still a stand-in for a websocket: the list is only refetched when the
        // count actually moves. The menu renders straight from the signal and
        // does not fetch on open, so dropping this would show a stale list.
        if (
          res.unread_count > 0 &&
          res.unread_count !== this.notificationCount()?.unread_count
        ) {
          this.setNotifications()
        }
        this.notificationCount.set(res)
      })
    )
  }

  /**
   * this is a function to set to zero inmediately the unread notification count
   * this is for when the notification are marked as viewed, as this is done with an individual http call
   * for each one of the notification, the user would not see inmediately that there is no unread notification
   * that is why we need this to inmediately set the count to zero so it sees that there is no more notification unread
   *
   * this also should be deprecated when the websocket is implemented
   */
  public setNotificationCountToZero() {
    this.notificationCount.update(val => {
      return { ...val, unread_count: 0 }
    })

  }

  public toggleMenu(event: Event) {
    this.toggleMenuSubject.next(event)
  }

  public setNotifications() {
    this.notificationAPI.getAll('Notifications').subscribe(response => {
      this._notifications.set(response.items);
    });
  }
}
